/* WACC Homepage Animations (D9 / D27)
 *
 * Provides:
 *   1. Scroll-triggered fade-ins on .wacc-section (and any [data-wacc-fade] element)
 *   2. Counter animation on [data-wacc-counter] when the element scrolls into view
 *   3. Pack shot hover lift via .wacc-hover-lift class
 *   4. Klaviyo onsite teaser offer-copy sync
 *
 * Honours prefers-reduced-motion.
 */
(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll-triggered fade-ins ---------- */

  const fadeStyle = document.createElement('style');
  fadeStyle.textContent = [
    '.wacc-fade-init { opacity: 0; transform: translateY(18px); transition: opacity .7s ease, transform .7s ease; will-change: opacity, transform; }',
    '.wacc-fade-init.is-in { opacity: 1; transform: translateY(0); }',
    '@media (prefers-reduced-motion: reduce) { .wacc-fade-init { opacity: 1; transform: none; transition: none; } }',
    '.wacc-hover-lift { transition: transform .25s ease, box-shadow .25s ease; will-change: transform; }',
    '.wacc-hover-lift:hover { transform: translateY(-6px); box-shadow: 0 18px 36px rgba(0,0,0,0.12); }',
    '@media (prefers-reduced-motion: reduce) { .wacc-hover-lift:hover { transform: none; box-shadow: none; } }',
  ].join('\n');
  document.head.appendChild(fadeStyle);

  function initFadeIns() {
    const sectionTargets = document.querySelectorAll('.wacc-section, [data-wacc-fade]');
    sectionTargets.forEach((el) => el.classList.add('wacc-fade-init'));
    const allTargets = document.querySelectorAll('.wacc-fade-init');
    if (!allTargets.length) return;

    if (!('IntersectionObserver' in window) || REDUCED) {
      allTargets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    allTargets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < viewportH * 0.92) {
        requestAnimationFrame(() => el.classList.add('is-in'));
      }
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -4% 0px' }
    );

    allTargets.forEach((el) => {
      if (!el.classList.contains('is-in')) io.observe(el);
    });

    setTimeout(() => {
      document.querySelectorAll('.wacc-fade-init').forEach((el) => {
        if (!el.classList.contains('is-in')) el.classList.add('is-in');
      });
    }, 2500);
  }

  /* ---------- 2. Counter animation ---------- */

  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function resolveTarget(el) {
    let target = parseFloat(el.dataset.target);
    if (!Number.isNaN(target) && target !== 0) return target;
    // Fallback: pull the first number out of the rendered textContent.
    const match = (el.textContent || '').match(/-?\d+(?:\.\d+)?/);
    if (match) {
      const parsed = parseFloat(match[0]);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return Number.isNaN(target) ? 0 : target;
  }

  function animateCounter(el) {
    if (el.dataset.waccCounterFired === '1') return;
    const target = resolveTarget(el);
    if (Number.isNaN(target) || target === 0) return;
    el.dataset.waccCounterFired = '1';

    const suffix = el.dataset.suffix || '';
    const duration = parseInt(el.dataset.duration || '1400', 10);

    if (REDUCED) {
      el.textContent = String(Math.round(target)) + suffix;
      return;
    }

    el.textContent = '0' + suffix;
    const start = performance.now();
    function step(now) {
      const t = Math.min(1, (now - start) / duration);
      const value = Math.round(target * easeOutCubic(t));
      el.textContent = String(value) + suffix;
      if (t < 1) requestAnimationFrame(step);
      else el.textContent = String(Math.round(target)) + suffix;
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    const counters = document.querySelectorAll('[data-wacc-counter]');
    if (!counters.length) return;

    if (!('IntersectionObserver' in window) || REDUCED) {
      counters.forEach(animateCounter);
      return;
    }

    // Reveal-on-first-paint: animate any counter already in (or near) view at boot
    // Same pattern as the fade-in pass. Catches the case where the IO never
    // fires because the element is already past the threshold by the time
    // observe() runs (post-defer load with a hash-anchored scroll, etc.).
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    counters.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < viewportH * 0.92 && rect.bottom > 0) {
        requestAnimationFrame(() => animateCounter(el));
      }
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px 0px 0px' }
    );

    counters.forEach((el) => {
      if (el.dataset.waccCounterFired !== '1') io.observe(el);
    });

    // Safety net: if a counter hasn't fired within 3.5s of being visible
    // (headless screenshot tools, mis-behaving IO under iframe preview, or
    // the section sat below the fold and IO silently missed it), force-fire
    // any counter that is currently in the viewport.
    setTimeout(() => {
      document.querySelectorAll('[data-wacc-counter]').forEach((el) => {
        if (el.dataset.waccCounterFired === '1') return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < vh && rect.bottom > 0) animateCounter(el);
      });
    }, 3500);
  }

  /* ---------- 3. Pack shot hover lift (auto-decorate) ---------- */

  function initHoverLift() {
    const liftSelectors = [
      '.wacc-categories__tile',
      '.wacc-quiz__pack-group',
    ];
    document.querySelectorAll(liftSelectors.join(',')).forEach((el) => {
      el.classList.add('wacc-hover-lift');
    });
  }

  /* ---------- 4. Klaviyo onsite offer copy ---------- */

  function initKlaviyoOfferCopy() {
    const offerCopy = 'Free delivery + 10% off your first order';
    const teaserCopy = offerCopy;
    const ctaCopy = 'Get instant code';
    const ctaCopyCaps = 'GET INSTANT CODE';
    const successTitle = 'Your first-order offer is ready';
    const successBody = 'Use codes WELCOME10 and FIRSTSHIP at checkout today.';
    const copyRules = [
      { pattern: /Join\s+Us\s*[-,]\s*10%\s+off\s+your\s+first\s+coffee\s+order/gi, value: offerCopy },
      { pattern: /Get\s+15%\s+Off/gi, value: teaserCopy },
      { pattern: /Get\s+10%\s+Off/gi, value: teaserCopy },
      { pattern: /15%\s+off\s+your\s+first\s+(?:coffee\s+)?order/gi, value: offerCopy },
      { pattern: /10%\s+off\s+your\s+first\s+(?:coffee\s+)?order/gi, value: offerCopy },
      { pattern: /Thanks\s+for\s+subscribing!?/gi, value: successTitle },
      { pattern: /Your\s+welcome\s+code\s+is\s+on\s+its\s+way\s+to\s+your\s+inbox\.?/gi, value: successBody },
      { pattern: /JOIN\s+THE\s+CLUB/g, value: ctaCopyCaps },
      { pattern: /JOIN\s+THE\s+CO/g, value: ctaCopyCaps },
      { pattern: /Get\s+My\s+Code/gi, value: ctaCopy },
      { pattern: /Claim\s+Code/gi, value: ctaCopy },
    ];
    const teaserLabels = [
      'Get 10% Off',
      'Get 15% Off',
      'Join the inside track',
      'Join the inside track.',
      teaserCopy,
    ];
    const teaserStyle = document.createElement('style');
    teaserStyle.textContent = [
      '.wacc-klaviyo-teaser-normalized { left: 96px !important; bottom: 18px !important; }',
      '@media (max-width: 749px) { .wacc-klaviyo-teaser-normalized { left: 88px !important; max-width: calc(100vw - 112px) !important; } }',
    ].join('\n');
    document.head.appendChild(teaserStyle);

    const needsSync = (text) => copyRules.some((rule) => {
      rule.pattern.lastIndex = 0;
      return rule.pattern.test(text || '');
    });
    const syncCopy = (text) => copyRules.reduce((value, rule) => {
      rule.pattern.lastIndex = 0;
      return value.replace(rule.pattern, rule.value);
    }, text);

    function isKlaviyoContext(node) {
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      if (!el || !el.closest) return false;
      return Boolean(el.closest('[class*="klaviyo"], [id*="klaviyo"], [class*="kl-"], .needsclick'));
    }

    function isLikelyOnsiteTeaser(el) {
      if (!el || !el.getBoundingClientRect) return false;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      return rect.left < 260 && rect.bottom > viewportH - 180 && rect.width > 40 && rect.height > 20;
    }

    function findFixedTeaserContainer(el) {
      let current = el;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        if ((style.position === 'fixed' || style.position === 'sticky') && isLikelyOnsiteTeaser(current)) {
          return current;
        }
        current = current.parentElement;
      }
      return isLikelyOnsiteTeaser(el) ? el : null;
    }

    function normalizeTeaserPlacement(root) {
      const scope = root && root.querySelectorAll ? root : document;
      const candidates = scope.querySelectorAll('button, a, [role="button"], div, span, p');
      candidates.forEach((el) => {
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (!teaserLabels.includes(text)) return;
        const container = findFixedTeaserContainer(el);
        if (container) container.classList.add('wacc-klaviyo-teaser-normalized');
      });
    }

    function syncText(root) {
      if (!root) return;
      if (root.nodeType === Node.TEXT_NODE) {
        if (needsSync(root.nodeValue) && (isKlaviyoContext(root) || isLikelyOnsiteTeaser(root.parentElement))) {
          root.nodeValue = syncCopy(root.nodeValue);
        }
        return;
      }

      if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          return needsSync(node.nodeValue) && (isKlaviyoContext(node) || isLikelyOnsiteTeaser(node.parentElement))
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      });

      const textNodes = [];
      let current = walker.nextNode();
      while (current) {
        textNodes.push(current);
        current = walker.nextNode();
      }

      textNodes.forEach((node) => {
        node.nodeValue = syncCopy(node.nodeValue);
      });
      normalizeTeaserPlacement(root);
    }

    syncText(document.body);
    normalizeTeaserPlacement(document.body);

    if (!('MutationObserver' in window)) return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          syncText(mutation.target);
          normalizeTeaserPlacement(mutation.target.parentElement);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          syncText(node);
          normalizeTeaserPlacement(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    setTimeout(() => {
      syncText(document.body);
      normalizeTeaserPlacement(document.body);
    }, 1500);
  }

  /* ---------- Boot ---------- */

  function safe(fn, label) {
    try { fn(); } catch (e) {
      // Don't let one init failure kill the rest of the boot sequence.
      if (window.console) console.warn('[wacc-animations] ' + label + ' failed:', e);
    }
  }

  function boot() {
    safe(initFadeIns, 'initFadeIns');
    safe(initCounters, 'initCounters');
    safe(initHoverLift, 'initHoverLift');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
